import { useRecipient } from '../components/settings/shared/settings-queries';
import { useConversations } from './conversations';
import { onboardingRouteDecision, type OnboardingRoute } from './onboarding-route';

export interface OnboardingEntry {
  // The resolved route AFTER the resume→kickoff fallback below: a `resume` with nothing to resume becomes
  // a fresh `kickoff`, so a consumer never has to handle "resume but no thread".
  route: OnboardingRoute;
  resumeConversationId: string | null;
  // Both the recipient AND the conversation list have settled, so the decision is trustworthy. Until then
  // it is `none`/`false` and consumers must HOLD (show a loader, don't commit to the bare home) rather than
  // act on an unresolved guess — the post-signup index lag makes "not loaded yet" common.
  resolved: boolean;
}

// The onboarding conversation to resume is a first-party (web/app) thread; an externally-owned WhatsApp/SMS
// thread is never the onboarding chat. Undefined channel = a plain web-created conversation.
const RESUMABLE_CHANNELS = new Set(['WEB', 'APP']);

/**
 * The web onboarding-entry decision, DERIVED from server state (the recipient's onboarding status + whether
 * any conversation exists) rather than a `?welcome=true` URL param. This is the single place the client
 * classifies onboarding entry; it wraps the pure `onboardingRouteDecision` (the native port — see
 * `lib/onboarding-route.ts`) over the shared React-Query caches, so every consumer agrees and no navigation
 * path can strand a user by forgetting a flag.
 *
 * The in-session once-guard (`kickoffAlreadyFired`) lives at the ACTION site (the AppFrame trigger / the
 * ChatSurface autostart ref), not here — this hook reports the natural route so the presentation consumers
 * (overlay open, chat size) keep reading it while the greeting is in flight.
 */
export function useOnboardingEntry(): OnboardingEntry {
  const { data: recipient, isLoading: recipientLoading, isError: recipientError } = useRecipient();
  const { state } = useConversations(true);

  const conversationsReady = state.kind === 'ready';
  const conversations = conversationsReady ? state.conversations : [];
  const resolved = conversationsReady && !recipientLoading && !recipientError && !!recipient;

  if (!resolved) return { route: 'none', resumeConversationId: null, resolved: false };

  // The list is already newest-first (conversations data layer), so the first first-party thread is the one
  // to resume.
  const resumeConversationId =
    conversations.find((c) => !c.communicationChannel || RESUMABLE_CHANNELS.has(c.communicationChannel))?.id ?? null;

  const route = onboardingRouteDecision({
    onboardingState: recipient.onboardingState,
    finishedOnboarding: recipient.finishedOnboarding,
    hadStoredConversation: conversations.length > 0,
    kickoffAlreadyFired: false,
  });

  // native `enterOnboardingResume` falls back to a fresh kickoff when there is no thread to reopen.
  if (route === 'resume' && !resumeConversationId) return { route: 'kickoff', resumeConversationId: null, resolved: true };
  return { route, resumeConversationId: route === 'resume' ? resumeConversationId : null, resolved: true };
}
